import { Injectable } from '@angular/core';
import { Network } from '@ionic-native/network';
import { Platform } from 'ionic-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

/**
 * Estado de conectividade do app, combinando o plugin Network (Cordova) com a API do navegador.
 *
 * Antes disso a única checagem era `network.type === 'none'` dentro do RequestService, que no
 * PWA nunca dispara (`network.type` é undefined no navegador), e cada página de polling mantinha
 * a sua própria cópia dos listeners de online/offline.
 */
@Injectable()
export class ConnectivityService {
  private onlineSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private initialized: boolean;

  constructor(
    private network: Network,
    private platform: Platform
  ) {}

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.onlineSubject.next(this.readCurrentState());

    window.addEventListener('online', () => {
      this.onlineSubject.next(true);
    });
    window.addEventListener('offline', () => {
      this.onlineSubject.next(false);
    });
    // Ao voltar para a aba, reconcilia: a transição pode ter ocorrido com a aba congelada
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.onlineSubject.next(this.readCurrentState());
      }
    });

    if (this.platform.is('cordova')) {
      this.network.onConnect().subscribe(() => {
        this.onlineSubject.next(true);
      });
      this.network.onDisconnect().subscribe(() => {
        this.onlineSubject.next(false);
      });
    }
  }

  /**
   * Lê o estado real na hora, em vez de devolver um valor guardado.
   *
   * Os eventos online/offline só disparam na TRANSIÇÃO, e a transição pode acontecer com a aba
   * congelada em background — o evento se perde e um estado guardado fica travado em "offline"
   * para sempre, derrubando todas as requisições até o usuário recarregar a página.
   */
  isOnline(): boolean {
    return this.readCurrentState();
  }

  get online$(): Observable<boolean> {
    return this.onlineSubject.asObservable().pipe(distinctUntilChanged());
  }

  /**
   * Só considera offline com evidência forte. No PWA `network.type` é undefined, e tratá-lo
   * como offline bloquearia todas as requisições do navegador.
   */
  private readCurrentState(): boolean {
    if (this.platform.is('cordova') && this.network.type) {
      return this.network.type !== 'none';
    }
    if (typeof navigator.onLine === 'boolean') {
      return navigator.onLine;
    }
    return true;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/connectivity.service.ts